import { InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import {
  contactInformationSchema,
  ILegalClientContactInformation,
  ILegalClientContactInformationType,
  initContactInformation,
} from '../../Interfaces/ILegalClientContactInformation';

interface IProps {
  selectedContactInformation?: ILegalClientContactInformation;
  contactInformationTypes: ILegalClientContactInformationType[];
  unmount: () => void;
  onSave: (contactInformation: ILegalClientContactInformation) => void;
}

export const UpdateOrCreateContactInformationDialog = ({
  selectedContactInformation,
  contactInformationTypes,
  unmount,
  onSave,
}: IProps) => {
  const [formValues, setFormValues] = useState<ILegalClientContactInformation>(
    selectedContactInformation ?? initContactInformation()
  );

  const { showAlert } = useAlert();

  const onSubmit = () => {
    // Check if the selected contact type is email.
    if (
      formValues.idContactInformationType === 5 ||
      formValues.ContactInformationType.toLowerCase().includes('mail')
    ) {
      // Basic email regex validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formValues.Contact)) {
        showAlert({
          text: 'Bitte geben Sie eine gültige E-Mail ein',
          severity: 'error',
        });
        return;
      }
    }

    const parsedContactInformation =
      contactInformationSchema.safeParse(formValues);

    if (parsedContactInformation.success) {
      onSave(parsedContactInformation.data);
      unmount();
    } else {
      showAlert({
        text: 'Bitte füllen Sie alle Felder aus',
        severity: 'error',
      });
    }
  };

  return (
    <Dialog onClose={unmount} open={true} fullWidth maxWidth='xs'>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {selectedContactInformation
          ? 'Kontaktinformation bearbeiten'
          : 'Kontaktinformation hinzufügen'}
        <Tooltip
          title='Achtung! Die hier hinterlegten Kontaktdaten dienen der internen Information. Im Mandantenportal wird durch Angaben in diesen Feldern kein Account angelegt. Einen Mandantenaccount können Sie ausschließlich über das Feld „E-Mail-Adresse“ in der Hauptmaske des Mandanten anlegen bzw. vorbereiten.'
          arrow
        >
          <IconButton
            size='small'
            aria-label='Information'
            sx={{ color: 'red', padding: '4px' }}
          >
            <InfoOutlined fontSize='medium' />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Box
          pt={0.75}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <FormControl fullWidth>
            <InputLabel>Kontaktart</InputLabel>
            <Select
              label='Kontaktart'
              size='small'
              fullWidth
              value={formValues?.idContactInformationType}
              onChange={({ target }) => {
                const type = contactInformationTypes.find(
                  t => t.idContactInformationType === Number(target.value)
                )?.ContactInformationType;
                setFormValues(prev => ({
                  ...prev,
                  ContactInformationType: type ?? '',
                  idContactInformationType: Number(target.value),
                }));
              }}
            >
              {contactInformationTypes.map(
                ({ idContactInformationType, ContactInformationType }, i) => (
                  <MenuItem key={i} value={idContactInformationType}>
                    {ContactInformationType}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <TextField
            required
            label={`${formValues.ContactInformationType} eingeben`}
            size='small'
            value={formValues.Contact}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFormValues({
                ...formValues,
                Contact: event.target.value,
              })
            }
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={unmount}>
          Abbrechen
        </Button>
        <Button onClick={onSubmit} variant='contained'>
          {selectedContactInformation ? 'Speichern' : 'Hinzufügen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
